import React, {useEffect, useState} from 'react'
import { useForm } from "react-hook-form";
import {useTranslation} from "react-i18next";

import Typography from '@mui/material/Typography';
import VisibilityOff from '@mui/icons-material/VisibilityOffSharp';
import { RemoveRedEye as RemoveRedEyeIcon } from '@vedmarket/ui-kit';
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import InputAdornment from "@mui/material/InputAdornment";
import TextField from '@mui/material/TextField'
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Collapse } from '@mui/material';

import { useAuthContext } from "../../../context/AuthContext";
import {saveSignInSession, useSignInMutation} from "../../../api/auth";
import { useDispatch } from 'react-redux';
import { resetCalculationSession } from '../../../utils/calculationSession';
import { Header, Notification, NOTIFICATION_TYPE } from '@vedmarket/ui-kit';
import {ROUTES} from "../../../config/constants";
import {EMAIL_PATTERN} from "../../../utils/validation";
import {useLazyGetDictionaryLanguagesQuery} from "../../../api/localization";
import {changeAppLanguage, getLanguageCode} from "../../../config/i18";

import './../auth.scss'
import './login.scss'

enum LOGIN_FORM {
  EMAIL = 'email',
  PASSWORD = 'password',
  REMEMBER = 'remember',
}

type LoginForm = {
  email: string
  password: string
  remember: boolean
}

const Login = () => {

  const { setUser, user, isSuccess } = useAuthContext();
  const dispatch = useDispatch();

  const [ isVisiblePassword, setIsVisiblePassword ] = useState(false);

  const [ isLoading, setIsLoading ] = useState(false);

  const [ error, setError ] = useState<boolean>(false);

  const [ displayError, setDisplayError ] = useState<string>('')

  const [ signIn ] = useSignInMutation()

  const { t, i18n } = useTranslation()

  const [ localizationGetLanguagesRequest, { data: languages } ] = useLazyGetDictionaryLanguagesQuery();

  const { register,  handleSubmit, reset, formState: { errors, isValid } } = useForm<LoginForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      [LOGIN_FORM.REMEMBER]: true,
    },
    criteriaMode: "all",
  })

  const { ref: rememberInputRef, ...rememberInput } = register(LOGIN_FORM.REMEMBER);

  const selectedLanguage = languages?.find(({ code }) => getLanguageCode(code) === getLanguageCode(i18n.language))?.code;

  useEffect(() => {
    localizationGetLanguagesRequest('');
  }, [ localizationGetLanguagesRequest ]);

  useEffect(() => {
    if (isSuccess && user) {
      window.location.replace(`/${ROUTES.CABINET}/${ROUTES.CALCULATIONS}`);
    }
  }, [ user, isSuccess ])

  const onSubmit = handleSubmit (async (values) => {

    setIsLoading(true);

    try {
      const value = {
        email: values.email,
        password: values.password,
      };

      const response: any = await signIn(value).unwrap();

      if (response?.data?.detail) {

        setError(true);
        setDisplayError(response?.data?.detail)

        setTimeout(() => {
          setError(false)
        }, 5000);

        throw response?.error;
      } else {

        saveSignInSession({ jwt: response?.jwt, refresh: response?.refresh, remember: values.remember });

        resetCalculationSession(dispatch);

        // set the user
        setUser(response?.user);

        window.location.replace(`/${ROUTES.CABINET}/${ROUTES.CALCULATIONS}`);
      }
    } catch (error) {

      setError(true);
      // @ts-ignore
      setDisplayError(error?.data?.detail || t('errors__message__commonError'));

      setTimeout(() => {
        setError(false)
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  })


  return (
    <>
      <Header
        isEmpty
        languages={languages}
        user={{ lang: selectedLanguage || getLanguageCode(i18n.language) } as MeParamsOutput}
        changeLanguage={changeAppLanguage}
      />
      <form className='auth login' onSubmit={onSubmit}>
        <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: '-1.15px', marginBottom: '22px' }}>
          {t('common__text__login')}
        </Typography>
        <Card sx={{
          boxShadow: 'none',
        }}>
          <CardContent className='auth__card' sx={{ padding: '24px' }}>
            <div className='login__form'>
              <TextField
                {...register(LOGIN_FORM.EMAIL, { required: t('common__text__isRequired'), pattern: { value: EMAIL_PATTERN, message: t('errors__message__incorrectEmail') } })}
                error={Boolean(errors[LOGIN_FORM.EMAIL])}
                variant='outlined'
                label={t('common__text__email')}
                helperText={errors[LOGIN_FORM.EMAIL]?.message}
                autoComplete='email'
              />
              <TextField
                {...register(LOGIN_FORM.PASSWORD, { required: t('common__text__isRequired'), minLength: { value: 8, message: `8 ${t('common__text__minLength')}` } })}
                error={Boolean(errors[LOGIN_FORM.PASSWORD])}
                helperText={errors[LOGIN_FORM.PASSWORD]?.message}
                variant='outlined'
                label={t('common__text__password')}
                type={isVisiblePassword? 'text' : 'password'}
                sx={{
                  marginTop: 1,
                }}
                className='login__password-field'
                InputProps={{
                  endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePassword(!isVisiblePassword)} className='auth__input-adornment'>{isVisiblePassword ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
                }}
                autoComplete='password'
              />
              <div className='login__password-wrapper'>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox defaultChecked inputRef={rememberInputRef} {...rememberInput} />}
                    label={t('actions__text__rememberMe')}
                    sx={{
                      '.MuiFormControlLabel-label': {
                        fontSize: '16px',
                      }
                    }}
                  />
                </FormGroup>
                <Link
                  href={`${ROUTES.FORGOT_PASSWORD}`}
                  className='auth__forget-password'
                  onClick={() => reset()}
                >{t('actions__message__forgotPassword')}</Link>
              </div>
            </div>
            <Button
              variant='contained'
              sx={{
                marginTop: 2
              }}
              disabled={!isValid || isLoading}
              type='submit'
            >{isLoading ? t('common__message__loadingText') : t('actions__text__login')}</Button>
            <Collapse in={Boolean(error)} timeout={400}>
              <Notification
                type={NOTIFICATION_TYPE.ERROR}
                sx={{ marginTop: 2 }}
                noCloseButton
                title={displayError}
              />
            </Collapse>
          </CardContent>
        </Card>
        <Typography variant='body1' sx={{ textAlign: 'center', marginTop: 3 }}>
          {t('actions__message__notAMember')}&nbsp;
          <Link href={ROUTES.REGISTER} onClick={() => reset()}>{t('common__text__registration')}</Link>
        </Typography>
      </form>
    </>
  )
}

export default Login
